/**
 * MER-715 ratio contract: the fold behind DERIVED_RATIOS tiles, and the one
 * stored-ratio shape SUM(num) / SUM(denom) cannot reach because a component was
 * never persisted.
 *
 * The contract: a ratio on screen is SUM(numerator) / SUM(denominator) over the
 * selected rows, never an average of stored ratios. DERIVED_RATIOS in
 * queries.ts is that path, and foldDerivedRatio below is its outcome.
 *
 *   foldSingleRow (MER-684). yards_per_haul and hours_per_haul are stored per
 *   (period, division) row, but the haul-only numerator and the matched haul
 *   count they divide are discarded at write time. One row is an honest
 *   reading of the stored ratio. Two or more rows can only be combined by
 *   weighting, and the weight we would have to use is not the real
 *   denominator, so the fold returns `uncombinable` and the tile says so.
 *
 * Pure and dependency-free so node:test imports it directly; the .d.mts beside
 * it types it for queries.ts.
 */

function toNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * The DERIVED_RATIOS SUM(numerator) / SUM(denominator) outcome (MER-317 and
 * MER-695 semantics). A null sum means no source rows; a zero denominator is
 * 0/0, not a rate. `zeroNumeratorIsMissing` marks cost shares whose numerator
 * is zero only when its feed carried nothing (no hauler has a month with no
 * maintenance), so a zero there over real revenue is `missing`, never a
 * perfect 0.0%.
 * Returns {state: "empty"} | {state: "value", value} | {state: "missing"}.
 */
export function foldDerivedRatio(numerator, denominator, opts = {}) {
  if (numerator === null || numerator === undefined) return { state: "empty" };
  if (denominator === null || denominator === undefined || denominator === 0) {
    return { state: "empty" };
  }
  if (opts.zeroNumeratorIsMissing === true && numerator === 0) return { state: "missing" };
  // MER-720: a coverage floor. Some ratios are computed over a SUBSET of the
  // population the tile is filtered to, and the answer only describes that
  // selection while the subset is most of it. Disposal $/ton divides ton-priced
  // cost by ton-priced tons, but Delta tips over half its volume at facilities
  // that bill by the cubic yard, so a Hattiesburg roll-off month could publish
  // "$46.20 per ton" off 0.10% of that month's disposal spend. The price was
  // real; the claim that it described the month was not. Withheld rather than
  // shown, because a plausible wrong number is worse than a blank.
  const cover = toNumber(opts.coverage);
  const whole = toNumber(opts.coverageOf);
  const floor = toNumber(opts.coverageFloor);
  if (floor !== null && whole !== null && whole > 0) {
    if (cover === null || cover / whole < floor) return { state: "missing" };
  }
  return { state: "value", value: numerator / denominator };
}

/**
 * One stored ratio, never a combination of several.
 * Returns {state: "empty"} | {state: "value", value} | {state: "uncombinable"}.
 */
export function foldSingleRow(rows, column) {
  const values = [];
  for (const row of rows ?? []) {
    const v = toNumber(row?.[column]);
    if (v !== null) values.push(v);
  }
  if (values.length === 0) return { state: "empty" };
  if (values.length > 1) return { state: "uncombinable" };
  return { state: "value", value: values[0] };
}
